import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { FileViewer } from "@capacitor/file-viewer";
import { AndroidActions } from "./androidActions";

const CHUNK_BYTES = 192 * 1024;
const preparedFiles = new WeakMap<Blob, Map<string, Promise<string>>>();

export async function prepareNativeExport(blob: Blob, filename: string, extension: "pdf" | "mir"): Promise<string> {
  if (!blob.size || (extension === "pdf" && blob.type !== "application/pdf")) throw new Error("Prepare your report before sharing it.");
  const safeFilename = filename.replace(/[^a-zA-Z0-9._-]/g, "-").replace(/\.(pdf|mir)$/i, "").slice(0, 116) + `.${extension}`;
  const path = `report-exports/${crypto.randomUUID()}/${safeFilename || "move-in-report.pdf"}`;
  try {
    // Bounded bridge messages; native storage contains decoded binary bytes.
    // Never construct a base64 representation of the entire report.
    for (let offset = 0; offset < blob.size; offset += CHUNK_BYTES) {
      const bytes = new Uint8Array(await blob.slice(offset, offset + CHUNK_BYTES).arrayBuffer());
      let binary = "";
      for (let index = 0; index < bytes.length; index += 8192) {
        binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
      }
      const data = btoa(binary);
      if (offset === 0) await Filesystem.writeFile({ path, directory: Directory.Cache, data, recursive: true });
      else await Filesystem.appendFile({ path, directory: Directory.Cache, data });
    }
  } catch {
    await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
    throw new Error("Could not prepare the file for sharing. Free some device storage and try again.");
  }
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
  return uri;
}

async function preparePdf(blob: Blob, filename: string): Promise<string> {
  let files = preparedFiles.get(blob);
  if (!files) { files = new Map(); preparedFiles.set(blob, files); }
  let pending = files.get(filename);
  if (pending) {
    const uri = await pending;
    try { await Filesystem.stat({ path: uri }); return uri; }
    catch { files.delete(filename); }
  }
  pending = prepareNativeExport(blob, filename, "pdf").catch((error: unknown) => { files.delete(filename); throw error; });
  files.set(filename, pending);
  return pending;
}

export async function openPdf(blob: Blob, filename: string): Promise<void> {
  const path = await preparePdf(blob, filename);
  await FileViewer.openDocumentFromLocalPath({ path });
}

export async function savePdf(blob: Blob, filename: string): Promise<boolean> {
  const uri = await preparePdf(blob, filename);
  return savePdfFile(uri);
}

export async function savePdfFile(uri: string): Promise<boolean> {
  return (await AndroidActions.savePdf({ uri })).saved;
}

export async function sharePdf(blob: Blob, filename: string): Promise<void> {
  const uri = await preparePdf(blob, filename);
  await sharePdfFile(uri);
}

export async function sharePdfFile(uri: string): Promise<void> {
  // Do not delete on return: a viewer or receiving app may still be reading it.
  await Share.share({ files: [uri], title: "Move-in condition report", dialogTitle: "Save or share your report" });
}
